import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import PasswordField from "@/components/PasswordField";
import { useAuth, API } from "@/context/AuthContext";
import { Briefcase, Factory, ArrowUpRight } from "lucide-react";

const roleName = (role) => (role === "buyer" ? "bidder" : "manufacturer");

export default function Login() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [role, setRole] = useState("buyer");
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [policyConsent, setPolicyConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const googleButton = useRef(null);
  const policyConsentRef = useRef(policyConsent);
  policyConsentRef.current = policyConsent;

  const finishLogin = useCallback((data) => {
    setUser(data);
    if (data.role === "admin") nav("/admin");
    else if (data.role === "unassigned") nav("/select-role");
    else nav("/dashboard");
  }, [nav, setUser]);

  const handleGoogleCredential = useCallback(async (credential) => {
    if (!policyConsentRef.current) {
      setError("Accept the platform terms and privacy practices before signing in with Google.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/google`, { credential, desired_role: role, policy_consent: policyConsentRef.current });
      finishLogin(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Google sign-in could not be completed.");
    } finally {
      setSubmitting(false);
    }
  }, [finishLogin, role]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const container = googleButton.current;
    if (!container) return undefined;
    container.replaceChildren();
    if (!clientId || mode === "reset") return undefined;
    const render = () => {
      if (!window.google?.accounts?.id || !container) return;
      window.google.accounts.id.initialize({ client_id: clientId, callback: ({ credential }) => handleGoogleCredential(credential) });
      window.google.accounts.id.renderButton(container, { theme: "outline", size: "large", width: 420, text: "continue_with" });
    };
    if (window.google?.accounts?.id) render();
    const script = document.querySelector("script[data-google-identity]");
    if (!window.google?.accounts?.id) script?.addEventListener("load", render, { once: true });
    return () => {
      script?.removeEventListener("load", render);
      container.replaceChildren();
    };
  }, [handleGoogleCredential, mode, policyConsent]);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      if (mode === "register") {
        const { data } = await axios.post(`${API}/auth/password/signup`, {
          name, email, password, desired_role: role, policy_consent: policyConsent,
        });
        setMessage(data.message);
      } else if (mode === "reset") {
        const { data } = await axios.post(`${API}/auth/password/reset/request`, { email });
        setMessage(data.message);
      } else {
        const { data } = await axios.post(`${API}/auth/password/login`, { email, password, policy_consent: policyConsent });
        finishLogin(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "We could not complete your request.");
    } finally {
      setSubmitting(false);
    }
  };

  const setScreen = (next) => {
    setMode(next);
    setError("");
    setMessage("");
  };

  const heading = mode === "register" ? "Create your account." : mode === "reset" ? "Reset your password." : "Welcome back.";
  return (
    <div className="min-h-screen bg-bone flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-5xl w-full">
          <div className="text-center mb-10 reveal">
            <div className="overline mb-4">§ SECURE ACCESS</div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light leading-[0.95] tracking-tight">{heading}</h1>
            <p className="mt-5 text-[--muted-foreground] text-lg max-w-xl mx-auto">
              {mode === "register" ? "Use Google or create a password-protected AskWinn account." : "Sign in to your AskWinn workspace."}
            </p>
          </div>

          {mode !== "reset" && (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <RoleCard selected={role === "buyer"} onClick={() => setRole("buyer")} icon={Briefcase} eyebrow="A — BIDDER" title="I'm building a product." description="Post briefs, compare manufacturer bids, and move your product forward." />
              <RoleCard selected={role === "agent"} onClick={() => setRole("agent")} icon={Factory} eyebrow="B — MANUFACTURER" title="I do end-to-end production." description="Create your factory profile and receive qualified RFQs after approval." accent />
            </div>
          )}

          <form onSubmit={submit} className="editorial-card p-6 max-w-2xl mx-auto">
            {mode !== "reset" && process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
              <>
                <div aria-disabled={!policyConsent} className={policyConsent ? "" : "pointer-events-none opacity-50"}>
                  <div ref={googleButton} className="flex justify-center mb-2 min-h-10" />
                </div>
                {!policyConsent && <p className="text-center text-xs text-[--muted-foreground] mb-5">Accept the platform terms and privacy practices to enable Google sign-in.</p>}
                <div className="flex items-center gap-3 text-xs text-[--muted-foreground] mb-5">
                  <span className="h-px bg-[--border-soft] flex-1" /><span>OR USE EMAIL</span><span className="h-px bg-[--border-soft] flex-1" />
                </div>
              </>
            ) : null}
            <div className="grid md:grid-cols-2 gap-4">
              {mode === "register" && <label className="block text-sm font-medium text-[--muted-foreground]">Full name
                <input className="mt-2 input-underline w-full" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
              </label>}
              <label className={`block text-sm font-medium text-[--muted-foreground] ${mode !== "register" ? "md:col-span-2" : ""}`}>Work email
                <input className="mt-2 input-underline w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required autoComplete="email" />
              </label>
              {mode !== "reset" && <PasswordField
                label="Password"
                className={mode === "login" ? "md:col-span-2" : ""}
                labelClassName="font-medium text-[--muted-foreground]"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={mode === "register" ? 12 : 1}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              >
                {mode === "register" && <span className="mt-2 block text-xs text-[--muted-foreground]">12+ characters with uppercase, lowercase, and a number.</span>}
              </PasswordField>}
            </div>
            {mode !== "reset" && <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[--muted-foreground]">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[--klein]" checked={policyConsent} onChange={(event) => setPolicyConsent(event.target.checked)} required />
              <span>I agree to AskWinn's platform terms and privacy practices. <Link className="underline text-ink" to="/trust-safety">Trust & Safety</Link>.</span>
            </label>}
            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
            <button type="submit" className="btn-primary mt-6 w-full justify-center" disabled={submitting || (mode !== "reset" && !policyConsent)}>
              {submitting ? "Please wait..." : mode === "register" ? `Create ${roleName(role)} account` : mode === "reset" ? "Send reset link" : "Sign in"}
            </button>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 justify-center text-sm">
              {mode === "login" && <button type="button" className="underline" onClick={() => setScreen("reset")}>Forgot password?</button>}
              {mode !== "register" && <button type="button" className="underline" onClick={() => setScreen("register")}>Create an account</button>}
              {mode !== "login" && <button type="button" className="underline" onClick={() => setScreen("login")}>Back to sign in</button>}
            </div>
          </form>
          <div className="text-center mt-10"><Link to="/" className="font-mono text-xs underline text-[--muted-foreground]">← BACK TO HOME</Link></div>
        </div>
      </main>
    </div>
  );
}

function RoleCard({ selected, onClick, icon: Icon, eyebrow, title, description, accent = false }) {
  return <button type="button" onClick={onClick} className={`editorial-card p-8 text-left group relative overflow-hidden ${selected ? "ring-2 ring-klein" : ""}`}>
    <Icon className={`w-9 h-9 mb-6 ${accent ? "text-burn" : "text-klein"}`} />
    <div className={`overline mb-3 ${accent ? "text-burn" : ""}`}>{eyebrow}</div>
    <h2 className="font-serif text-3xl leading-tight tracking-tight mb-3">{title}</h2>
    <p className="text-sm text-[--muted-foreground] leading-relaxed mb-6">{description}</p>
    <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.15em]"><span>Continue as {eyebrow.includes("BIDDER") ? "bidder" : "manufacturer"}</span><ArrowUpRight className="w-4 h-4" /></div>
  </button>;
}
